// src/pages/Seller/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  Button, Avatar, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, LinearProgress, Alert,
} from '@mui/material';
import {
  Inventory, ShoppingCart, AttachMoney, CheckCircle,
  Add, ArrowForward, Refresh, Receipt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { sellerAPI } from '../../services/api';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }); }
  catch { return '—'; }
};

const STATUS_COLOR = {
  pending:'warning', confirmed:'info', processing:'info',
  shipped:'primary', delivered:'success', cancelled:'error',
};

const SellerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats,     setStats]     = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [fruits,    setFruits]    = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, eRes, fRes] = await Promise.allSettled([
        sellerAPI.getDashboardStats(),
        sellerAPI.getMyEnquiries(),
        sellerAPI.getMyFruits(),
      ]);
      if (sRes.status === 'fulfilled') {
        const d = sRes.value.data;
        setStats(d.stats || d || {});
      }
      if (eRes.status === 'fulfilled') {
        const d = eRes.value.data;
        setEnquiries((Array.isArray(d) ? d : d.enquiries || []).slice(0, 5));
      }
      if (fRes.status === 'fulfilled') {
        const d = fRes.value.data;
        setFruits((Array.isArray(d) ? d : d.fruits || []).slice(0, 5));
      }
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const STAT_CARDS = [
    { label:'Total Products',  value: stats.total_products  || 0, color:'#2196f3', icon:<Inventory /> },
    { label:'Total Enquiries', value: stats.total_enquiries || 0, color:'#ff9800', icon:<ShoppingCart /> },
    { label:'Active Orders',   value: stats.total_orders    || 0, color:'#4caf50', icon:<CheckCircle /> },
    { label:'Total Revenue',   value: fmt(stats.total_revenue),   color:'#9c27b0', icon:<AttachMoney />, isText:true },
  ];

  if (loading) return (
    <Container sx={{ py:4 }}>
      <LinearProgress sx={{ mb:2 }} />
      <Typography color="text.secondary">Loading dashboard...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py:3 }}>

      {/* Header */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Welcome, {user?.company_name || user?.email}!
          </Typography>
          <Typography color="text.secondary">Seller Dashboard</Typography>
        </Box>
        <Box sx={{ display:'flex', gap:2 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAll}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/seller/products')}>
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb:4 }}>
        {STAT_CARDS.map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius:2 }}>
              <CardContent>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold"
                      sx={s.isText ? { fontSize:'1.3rem' } : {}}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor:`${s.color}20`, color:s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>

        {/* Recent Enquiries */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p:3, borderRadius:2 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
              <Typography variant="h6" fontWeight="bold">Recent Enquiries</Typography>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/seller/orders')}>
                View All
              </Button>
            </Box>
            {enquiries.length === 0 ? (
              <Alert severity="info">No enquiries yet. They will appear once buyers contact you.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enquiries.map(e => (
                      <TableRow key={e.id} hover>
                        <TableCell>#{e.id}</TableCell>
                        <TableCell>{e.product || e.productName || `Fruit #${e.fruit_id}`}</TableCell>
                        <TableCell>{e.quantity}</TableCell>
                        <TableCell>
                          <Chip label={e.status} size="small"
                            color={STATUS_COLOR[e.status] || 'default'}
                            sx={{ textTransform:'capitalize' }} />
                        </TableCell>
                        <TableCell>{fmtDate(e.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* My Products */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p:3, borderRadius:2 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
              <Typography variant="h6" fontWeight="bold">My Products</Typography>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/seller/products')}>
                View All
              </Button>
            </Box>
            {fruits.length === 0 ? (
              <Alert severity="info">
                No products yet.
                <Button size="small" sx={{ ml:1 }} onClick={() => navigate('/seller/products')}>
                  Add Product
                </Button>
              </Alert>
            ) : (
              <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
                {fruits.map(f => (
                  <Box key={f.id} sx={{ display:'flex', alignItems:'center', gap:2,
                                        p:1.5, bgcolor:'grey.50', borderRadius:2 }}>
                    <Avatar variant="rounded" sx={{ bgcolor:'primary.light', width:36, height:36 }}>
                      {(f.name || 'F').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex:1 }}>
                      <Typography variant="body2" fontWeight="medium">{f.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.category}</Typography>
                    </Box>
                    <Chip label={f.active !== false ? 'Active' : 'Inactive'} size="small"
                      color={f.active !== false ? 'success' : 'default'} />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Paper sx={{ p:3, borderRadius:2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Quick Actions</Typography>
            <Grid container spacing={2}>
              {[
                { label:'Add Product',   icon:<Add />,         path:'/seller/products', variant:'contained' },
                { label:'View Orders',   icon:<ShoppingCart />, path:'/seller/orders',   variant:'outlined' },
                { label:'View Invoices', icon:<Receipt />,      path:'/seller/invoices', variant:'outlined' },
              ].map((btn, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Button fullWidth variant={btn.variant} startIcon={btn.icon} size="large"
                    onClick={() => navigate(btn.path)}>
                    {btn.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SellerDashboard;